import React, { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from "react";
import { useRouter } from "next/router";
import classes from "./FilterCard.module.scss";
import Dropdown, { DropdownOption } from "../../../../UI/Dropdown/Dropdown";
import fetchUtil from "../../../../../utils/fetchUtil";
import CrossIcon from "../../../../UI/Icons/CrossIcon";
import useDebounce from "../../../../../hooks/useDebounce";

interface FilterCardProps {
  setScholarships: Dispatch<SetStateAction<any[]>>;
  setSearchedUni: Dispatch<SetStateAction<string>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
}

interface SearchOverrides {
  studyLevel?: DropdownOption[];
  scholarshipType?: DropdownOption | null;
  universitySearchTerm?: string;
}

const FilterCard = ({ setScholarships, setSearchedUni, setLoading }: FilterCardProps) => {
  const router = useRouter();
  const slug = (router.query.slug as string) ?? "";
  const [studyLevelOptions, setStudyLevelOptions] = useState<DropdownOption[]>([]);
  const [scholarshipTypeOptions, setScholarshipTypeOptions] = useState<DropdownOption[]>([]);
  const [scholarshipType, setScholarshipType] = useState<DropdownOption | null>(null);
  const [studyLevel, setStudyLevel] = useState<DropdownOption[]>([]);
  const [universitySearchTerm, setUniversitySearchTerm] = useState("");

  useEffect(() => {
    fetchUtil(`${process.env.NEXT_PUBLIC_API_BASE_URL}/new-country-specific-scholarships/program-filters?slug=${slug}`, "GET", {}, "", false).then(
      (res: any) => {
        const toOptions = (values: string[] = []): DropdownOption[] => values.map((value) => ({ label: value, value }));
        setStudyLevelOptions(toOptions(res?.studyLevel));
        setScholarshipTypeOptions(toOptions(res?.type));
      },
    );
  }, []);

  const scholarshipTypeSelectHandler = (option: DropdownOption) => {
    setScholarshipType(option);
    searchClickHandler({ scholarshipType: option });
  };

  const studyLevelSelectHandler = (selectedOptions: DropdownOption[]) => {
    setStudyLevel(selectedOptions);
    searchClickHandler({ studyLevel: selectedOptions });
  };

  const buildSearchQuery = (overrides: SearchOverrides = {}) => {
    const effectiveStudyLevel = overrides.studyLevel ?? studyLevel;
    const effectiveScholarshipType = "scholarshipType" in overrides ? overrides.scholarshipType : scholarshipType;
    const effectiveUniversitySearchTerm = overrides.universitySearchTerm ?? universitySearchTerm;

    const params = new URLSearchParams({ slug: slug });
    if (effectiveStudyLevel.length) params.set("studyLevelMultiSelect", effectiveStudyLevel.map((option) => option.value).join(","));
    if (effectiveScholarshipType?.value) params.set("type", effectiveScholarshipType.value);
    if (effectiveUniversitySearchTerm) params.set("subTitle", effectiveUniversitySearchTerm);
    return params.toString();
  };

  const searchClickHandler = (overrides: SearchOverrides = {}) => {
    setLoading(true);

    setSearchedUni(overrides.universitySearchTerm ?? universitySearchTerm);

    fetchUtil(
      `${process.env.NEXT_PUBLIC_API_BASE_URL}/new-country-specific-scholarships/search-programs?${buildSearchQuery(overrides)}`,
      "GET",
      {},
      "",
      false,
    )
      .then((res: any) => {
        setScholarships(res ?? []);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const debouncedSearch = useDebounce((value: string) => {
    searchClickHandler({ universitySearchTerm: value });
  });

  const inputChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUniversitySearchTerm(value);
    debouncedSearch(value);
  };

  return (
    <div className={classes.filterCardContainer}>
      <div className={classes.filterCard}>
        <div className={classes["row"]}>
          <Dropdown
            options={studyLevelOptions}
            multiple={true}
            selectedValues={studyLevel}
            placeholder="Study Level"
            onSelectionChange={studyLevelSelectHandler}
          />
          <Dropdown
            options={scholarshipTypeOptions}
            value={scholarshipType}
            showClear={true}
            placeholder="Type of Scholarship"
            onChange={scholarshipTypeSelectHandler}
            onClear={() => {
              setScholarshipType(null);
              searchClickHandler({ scholarshipType: null });
            }}
          />
        </div>
        <div className={classes["search-input-container"]}>
          <input
            className={classes.searchInput}
            value={universitySearchTerm}
            placeholder="Search by University.."
            onChange={inputChangeHandler}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                debouncedSearch.cancel();
                searchClickHandler();
              }
            }}
          />

          {universitySearchTerm && (
            <div
              className={classes.crossIconContainer}
              onClick={() => {
                setUniversitySearchTerm("");
                debouncedSearch.cancel();
                searchClickHandler({ universitySearchTerm: "" });
              }}
            >
              <CrossIcon width={12} height={12} color="#64647A" />
            </div>
          )}
        </div>

        {/* <button className={classes.searchBtn} onClick={searchClickHandler}>
          Search
        </button> */}
      </div>
    </div>
  );
};

export default FilterCard;
